import { memo, useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { KYCProviders } from "consts/kyc";
import {
    selectConstants,
    selectCountries,
    selectPSPs
} from "features/general/selectors";
import { selectPaymentMethodsData } from "features/paymentMethods/selectors";
import type { PspKycSettingCreatePayload } from "features/pspKycSettings/types";
import { useTypedSelector } from "hooks";
import Iconify from "lib/iconify";
import DeleteKycDialog from "./DeleteKycDialog";
import type { KycSettingsCardProps } from "./types";

const ROW_LABEL_WIDTH = 180;
const ALL_VALUE = '__all__';

type FieldName = keyof PspKycSettingCreatePayload;
type FieldValue = PspKycSettingCreatePayload[FieldName];

type Option = {
    value: string | number;
    label: string;
};

const FieldRow = ({ label, control }: { label: string; control: ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: `${ROW_LABEL_WIDTH}px 1fr`,
            alignItems: 'center',
            columnGap: 2,
            py: 1.5
        }}
    >
        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            {label}
        </Typography>
        {control}
    </Box>
);

const ReadOnlySelect = ({
    value,
    label,
    placeholder
}: {
    value: string | number | null | undefined;
    label: string;
    placeholder: string;
}) => {
    const resolvedValue = value === '' || value === null || value === undefined
        ? ALL_VALUE
        : value;

    return (
        <Select
            fullWidth
            size='small'
            disabled
            value={resolvedValue}
            displayEmpty
            renderValue={() => (
                <Typography
                    variant='body2'
                    color={label ? 'text.primary' : 'text.secondary'}
                >
                    {label || placeholder}
                </Typography>
            )}
        >
            <MenuItem value={resolvedValue}>{label || placeholder}</MenuItem>
        </Select>
    );
};

const EditableSelect = ({
    value,
    options,
    placeholder,
    error,
    disabled,
    onChange
}: {
    value: string | number;
    options: Array<Option>;
    placeholder: string;
    error?: string;
    disabled?: boolean;
    onChange: (value: string | number) => void;
}) => {
    const handleChange = (event: SelectChangeEvent<string | number>) => {
        const raw = event.target.value;
        const matched = options.find(opt => String(opt.value) === String(raw));
        onChange(matched ? matched.value : raw);
    };

    return (
        <FormControl fullWidth size='small' error={Boolean(error)} disabled={disabled}>
            <Select
                value={String(value ?? '')}
                onChange={handleChange}
                displayEmpty
                renderValue={selected => {
                    const match = options.find(opt => String(opt.value) === String(selected));
                    if (!match) {
                        return (
                            <Typography variant='body2' color='text.secondary'>
                                {placeholder}
                            </Typography>
                        );
                    }
                    return <Typography variant='body2'>{match.label}</Typography>;
                }}
            >
                {options.map(option => (
                    <MenuItem key={String(option.value)} value={String(option.value)} sx={{ fontWeight: 700 }}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
};

const KycSettingsCard = ({
    mode = 'view',
    setting,
    isLoading,
    isDeleting,
    error,
    onDelete,
    formValues,
    fieldErrors,
    isSubmitting,
    onFieldChange,
    onSave
}: KycSettingsCardProps) => {
    const constants = useTypedSelector(selectConstants);
    const countries = useTypedSelector(selectCountries());
    const PSPs = useTypedSelector(selectPSPs) as Array<string> | undefined;
    const paymentMethods = useTypedSelector(selectPaymentMethodsData());

    const isCreate = mode === 'create';
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const wasDeletingRef = useRef(false);

    useEffect(() => {
        if (wasDeletingRef.current && !isDeleting) {
            setIsDeleteDialogOpen(false);
        }
        wasDeletingRef.current = Boolean(isDeleting);
    }, [isDeleting]);

    const openDeleteDialog = () => setIsDeleteDialogOpen(true);
    const closeDeleteDialog = () => {
        if (isDeleting) return;
        setIsDeleteDialogOpen(false);
    };
    const handleConfirmDelete = () => {
        onDelete?.();
    };

    const countryByIso = useMemo(
        () => new Map<string, string>(
            countries.map(country => [country.iso2, country.shortName])
        ),
        [countries]
    );

    const kycTypeLabel = useMemo(() => {
        if (!setting) return '';
        const map = constants.KycTypesMap ?? {};
        const entry = Object.entries(map)
            .find(([, id]) => Number(id) === Number(setting.kycTypeId));
        return entry?.[0] ?? String(setting.kycTypeId);
    }, [constants.KycTypesMap, setting]);

    const pspLabel = setting?.PSP || 'All PSPs';
    const morLabel = setting?.MOR || 'All domains';
    const methodLabel = setting?.paymentMethodName
        || (setting?.methodId ? String(setting.methodId) : 'All methods');
    const countryLabel = setting?.countryIso2
        ? (countryByIso.get(setting.countryIso2) ?? setting.countryIso2)
        : 'All countries';
    const kycProviderLabel = setting?.kycProvider ?? '';

    // Create-mode option lists
    const pspOptions: Array<Option> = useMemo(
        () => (PSPs ?? []).map(p => ({ value: p, label: p })),
        [PSPs]
    );

    const kycProviderOptions: Array<Option> = useMemo(() => {
        const providers: Array<string> = constants.KycProviders ?? [];
        return providers
            .filter((p: string) => p !== KYCProviders.Manual)
            .map((p: string) => ({ value: p, label: p }));
    }, [constants.KycProviders]);

    const kycTypeOptions: Array<Option> = useMemo(() => {
        const map = constants.KycTypesMap ?? {};
        return Object.entries(map)
            .map(([name, id]) => ({ value: Number(id), label: name }))
            .sort((a, b) => (a.value as number) - (b.value as number));
    }, [constants.KycTypesMap]);

    const countryOptions: Array<Option> = useMemo(
        () => [
            { value: '', label: 'All countries' },
            ...countries.map(c => ({ value: c.iso2, label: c.shortName }))
        ],
        [countries]
    );

    const methodOptions: Array<Option> = useMemo(
        () => [
            { value: 0, label: 'All methods' },
            ...paymentMethods.map(pm => ({
                value: Number(pm.coreId),
                label: `${pm.name} (${pm.coreId})`
            }))
        ],
        [paymentMethods]
    );

    const renderViewRow = (label: string, control: ReactNode) => (
        <FieldRow
            label={label}
            control={isLoading
                ? <Skeleton variant='rounded' height={40} />
                : control}
        />
    );

    const handleField = <K extends FieldName>(name: K) =>
        (raw: FieldValue) => {
            if (!onFieldChange) return;
            onFieldChange(name, raw as PspKycSettingCreatePayload[K]);
        };

    return (
        <Card sx={{ boxShadow: theme => theme.shadows[3], pt: 0 }}>
            <CardContent sx={{ p: 3 }}>
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ mb: 1 }}
                >
                    <Typography variant='h6' sx={{ fontWeight: 700 }}>
                        KYC Settings
                    </Typography>
                    {!isCreate && (
                        <Button
                            size='small'
                            startIcon={<Iconify icon='solar:trash-bin-trash-bold' />}
                            disabled={!setting || isDeleting}
                            onClick={openDeleteDialog}
                            data-test-id='pspKycSettings.delete'
                            sx={{ color: '#7A0916' }}
                        >
                            Delete KYC
                        </Button>
                    )}
                </Stack>

                <Divider sx={{ mb: 1 }} />

                {isCreate ? (
                    <>
                        <FieldRow
                            label='MOR'
                            control={
                                <EditableSelect
                                    value=''
                                    options={[{ value: '', label: 'All domains' }]}
                                    placeholder='Select Domain'
                                    disabled
                                    onChange={() => undefined}
                                />
                            }
                        />
                        <FieldRow
                            label='Payment Method'
                            control={
                                <EditableSelect
                                    value={formValues?.methodId ?? 0}
                                    options={methodOptions}
                                    placeholder='Select Method'
                                    error={fieldErrors?.methodId}
                                    onChange={value => handleField('methodId')(Number(value))}
                                />
                            }
                        />
                        <FieldRow
                            label='PSP Provider'
                            control={
                                <EditableSelect
                                    value={formValues?.PSP ?? ''}
                                    options={pspOptions}
                                    placeholder='Select PSP Provider'
                                    error={fieldErrors?.PSP}
                                    onChange={value => handleField('PSP')(String(value))}
                                />
                            }
                        />
                        <FieldRow
                            label='KYC Provider'
                            control={
                                <EditableSelect
                                    value={formValues?.kycProvider ?? ''}
                                    options={kycProviderOptions}
                                    placeholder='Select KYC Provider'
                                    error={fieldErrors?.kycProvider}
                                    onChange={value => handleField('kycProvider')(String(value))}
                                />
                            }
                        />
                        <FieldRow
                            label='KYC Type'
                            control={
                                <EditableSelect
                                    value={
                                        typeof formValues?.kycTypeId === 'number'
                                            && formValues.kycTypeId >= 0
                                            ? formValues.kycTypeId
                                            : ''
                                    }
                                    options={kycTypeOptions}
                                    placeholder='Select KYC Type'
                                    error={fieldErrors?.kycTypeId}
                                    onChange={value => handleField('kycTypeId')(Number(value))}
                                />
                            }
                        />
                        <FieldRow
                            label='Country'
                            control={
                                <EditableSelect
                                    value={formValues?.countryIso2 ?? ''}
                                    options={countryOptions}
                                    placeholder='Select Country'
                                    error={fieldErrors?.countryIso2}
                                    onChange={value => handleField('countryIso2')(String(value))}
                                />
                            }
                        />
                    </>
                ) : (
                    <>
                        {renderViewRow(
                            'MOR',
                            <ReadOnlySelect
                                value={setting?.MOR}
                                label={morLabel}
                                placeholder='Select Domain'
                            />
                        )}
                        {renderViewRow(
                            'Payment Method',
                            <ReadOnlySelect
                                value={setting?.methodId}
                                label={methodLabel}
                                placeholder='Select Method'
                            />
                        )}
                        {renderViewRow(
                            'PSP Provider',
                            <ReadOnlySelect
                                value={setting?.PSP}
                                label={pspLabel}
                                placeholder='Select PSP Provider'
                            />
                        )}
                        {renderViewRow(
                            'KYC Provider',
                            <ReadOnlySelect
                                value={setting?.kycProvider}
                                label={kycProviderLabel}
                                placeholder='Select KYC Provider'
                            />
                        )}
                        {renderViewRow(
                            'KYC Type',
                            <ReadOnlySelect
                                value={setting?.kycTypeId}
                                label={kycTypeLabel}
                                placeholder='Select KYC Type'
                            />
                        )}
                        {renderViewRow(
                            'Country',
                            <ReadOnlySelect
                                value={setting?.countryIso2}
                                label={countryLabel}
                                placeholder='Select Country'
                            />
                        )}
                    </>
                )}

                {error && (
                    <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}

                {isCreate && (
                    <Stack direction='row' justifyContent='flex-end' sx={{ mt: 2 }}>
                        <Button
                            variant='contained'
                            disabled={isSubmitting}
                            onClick={onSave}
                            data-test-id='pspKycSettings.save'
                        >
                            {isSubmitting ? 'Saving…' : 'Save Settings'}
                        </Button>
                    </Stack>
                )}
            </CardContent>
            <DeleteKycDialog
                open={isDeleteDialogOpen}
                pspName={setting?.PSP ?? ''}
                isProcessing={Boolean(isDeleting)}
                onConfirm={handleConfirmDelete}
                onClose={closeDeleteDialog}
            />
        </Card>
    );
};

export default memo(KycSettingsCard);
